/**
 * Cookie-consent state (GDPR / ePrivacy).
 *
 * Only one cookie category on the platform requires prior consent — PostHog
 * analytics. Essential cookies (`access_token`, `refresh_token`, and the
 * Revolut checkout widget's fraud-prevention cookies) are exempt and always
 * active. The user's choice
 * is stored in localStorage (first-party technical storage, itself exempt)
 * and broadcast via window events so the React tree — and the PostHog
 * provider gate in `main.tsx` — can react immediately without a reload.
 *
 * The categories and wording mirror the Cookies Policy published on the CDN
 * (`LEGAL_DOCS.cookiesPolicy` in `src/config/legal.ts` — §2 cookie table, §4
 * consent management).
 */

export interface CookieConsent {
  /** Whether the user allowed analytics (PostHog) cookies. */
  analytics: boolean;
  /** ISO timestamp of when the choice was made. */
  decidedAt: string;
}

const STORAGE_KEY = 'tereiko-cookie-consent';
const CHANGE_EVENT = 'tereiko:cookie-consent-changed';
const OPEN_EVENT = 'tereiko:cookie-preferences-open';

/** The stored choice, or `null` when the user has not decided yet. */
export function getCookieConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    return typeof parsed.analytics === 'boolean' ? (parsed as CookieConsent) : null;
  } catch {
    return null;
  }
}

/** Persist a choice and notify subscribers. Revoking analytics also clears
 *  any PostHog cookies/storage left behind from a previous acceptance. */
export function setCookieConsent(analytics: boolean): CookieConsent {
  const consent: CookieConsent = { analytics, decidedAt: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  if (!analytics) clearAnalyticsArtifacts();
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  return consent;
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function subscribeCookieConsent(cb: (consent: CookieConsent | null) => void): () => void {
  const handler = () => cb(getCookieConsent());
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** Re-open the preferences panel (e.g. from the footer's "Cookie preferences"). */
export function openCookiePreferences(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** Subscribe to "open preferences" requests. Returns an unsubscribe function. */
export function subscribeCookiePreferencesOpen(cb: () => void): () => void {
  window.addEventListener(OPEN_EVENT, cb);
  return () => window.removeEventListener(OPEN_EVENT, cb);
}

/** Best-effort removal of PostHog cookies (`ph_*`) and local storage. */
function clearAnalyticsArtifacts(): void {
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie.split(';').forEach((entry) => {
    const name = entry.split('=')[0]?.trim();
    if (name && name.startsWith('ph_')) {
      document.cookie = `${name}=; ${expired}; path=/`;
      document.cookie = `${name}=; ${expired}; path=/; domain=.${window.location.hostname}`;
    }
  });
  Object.keys(localStorage)
    .filter((key) => key.startsWith('ph_'))
    .forEach((key) => localStorage.removeItem(key));
}
